import { useQuery } from '@tanstack/react-query';
import { CalendarClock } from 'lucide-react';
import { api } from '@/lib/api';
import { useShop } from '@/contexts/ShopContext';
import { useMemo } from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { useCurrentShopDetail } from '@/hooks/queries/useCurrentShopDetail';
import { dashboardV3NavItems, type NavItem, type NavChild } from '@/lib/navigation';

/**
 * Filtered list of nav items respecting permissions + shop feature flags.
 * Shared between desktop `Sidebar` (rail) and `MobileDrawerTwoPane` so the
 * visibility logic stays in one place.
 */
export function useVisibleNavItems(): NavItem[] {
  const { can } = usePermissions();
  const { currentShop } = useShop();
  const payroll = useQuery({
    queryKey: ['payroll', currentShop?.id, 'status'],
    queryFn: () => api.get<{ enabled: boolean; permissions: string[] }>('/payroll/status'),
    enabled: Boolean(currentShop?.id),
    staleTime: 30000,
    refetchInterval: 30000,
  });
  const payrollOperations = Boolean(
    payroll.data?.data.enabled &&
    payroll.data.data.permissions.some((p) =>
      [
        'attendance_read',
        'attendance_manage',
        'cycles_manage',
        'reminders_read',
        'reminders_manage',
        'prepare',
      ].includes(p),
    ),
  );

  const { data: shopDetailResponse } = useCurrentShopDetail();
  const driversEnabled = Boolean(shopDetailResponse?.data?.drivers_enabled);
  const returnsEnabled = Boolean(shopDetailResponse?.data?.returns_enabled);
  const barModeEnabled = Boolean(shopDetailResponse?.data?.bar_mode_enabled);
  const ingredientManagementEnabled = Boolean(
    shopDetailResponse?.data?.ingredient_management_enabled,
  );
  const partnerModeEnabled = Boolean(shopDetailResponse?.data?.partner_mode_enabled);
  const commercePagesEnabled = Boolean(shopDetailResponse?.data?.commerce_pages_enabled);
  const rentalModeEnabled = Boolean(shopDetailResponse?.data?.rental_mode_enabled);

  return useMemo(() => {
    const shopFlags: Record<string, boolean> = {
      drivers_enabled: driversEnabled,
      returns_enabled: returnsEnabled,
      bar_mode_enabled: barModeEnabled,
      ingredient_management_enabled: ingredientManagementEnabled,
      partner_mode_enabled: partnerModeEnabled,
      commerce_pages_enabled: commercePagesEnabled,
      rental_mode_enabled: rentalModeEnabled,
    };

    const filterChild = (child: NavChild): boolean => {
      if (child.permission && !can(child.permission)) return false;
      if (child.featureFlag && !shopFlags[child.featureFlag]) return false;
      return true;
    };

    return [
      ...dashboardV3NavItems,
      ...(payrollOperations
        ? [{ href: '/payroll/operations', labelKey: 'Pointage', icon: CalendarClock }]
        : []),
    ]
      .filter((item) => !item.permission || can(item.permission))
      .filter((item) => !item.featureFlag || shopFlags[item.featureFlag])
      .map((item) => ({
        ...item,
        children: item.children?.filter(filterChild),
      }))
      .filter((item) => !item.children || item.children.length > 0 || item.href);
  }, [
    can,
    payrollOperations,
    driversEnabled,
    returnsEnabled,
    barModeEnabled,
    ingredientManagementEnabled,
    partnerModeEnabled,
    commercePagesEnabled,
    rentalModeEnabled,
  ]);
}
